import clsx from 'clsx'
import { nothing, PropertyValueMap, unsafeCSS } from 'lit'
import { property, queryAssignedElements, state } from 'lit/decorators.js'
import { html } from 'lit/static-html.js'

import { createKeyboardNavigationHandler } from '../../internal/keyboard-navigation.js'
import DapDSPopup from '../popup/popup.component.js'
import styles from './command.scss?inline'

/**
 * `dap-ds-command`
 * @summary A command is a component that displays a list of commands.
 * @element dap-ds-command
 * @title - Command
 *
 * @event {{ value: string }} dds-change - Fired when the command value changes.
 *
 * @slot trigger - The trigger element for the command dropdown.
 * @slot - The command items.
 */
export default class DapDSCommand extends DapDSPopup {
  static tagName = 'dap-ds-command'

  static dependencies = {
    'dap-ds-popup': DapDSPopup,
  }

  constructor() {
    super()
    this.placement = 'bottom-start'
    this.overflow = true
  }

  @queryAssignedElements({ slot: 'trigger' })
  private readonly _trigger!: HTMLElement[]

  static readonly styles = unsafeCSS(styles)

  /**
   * The value of the command.
   */
  @property({ type: String })
  value: string | null = null

  /** Whether the command popup should take full width of the screen. */
  @property({ type: Boolean }) fullWidth: boolean = false

  /** ID of element that labels the command dropdown. */
  @property({ type: String, attribute: 'aria-labelledby' })
  ariaLabelledBy?: string

  @state()
  private _lastClickedItem: HTMLElement | null = null

  private _keyboardNavigation: ReturnType<
    typeof createKeyboardNavigationHandler
  > | null = null

  async connectedCallback() {
    await super.connectedCallback()
    this.addEventListener('keydown', this._handleKeyDown)
    // Bind methods to maintain proper context
    this.handleClick = this.handleClick.bind(this)
    this.handleDocumentMouseDown = this.handleDocumentMouseDown.bind(this)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('keydown', this._handleKeyDown)
    // Ensure document listener is cleaned up
    document.removeEventListener('mousedown', this.handleDocumentMouseDown)
  }

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties as Map<string, unknown>)

    if (this.value) {
      const item = this.querySelector(
        `dap-ds-command-item[value="${this.value}"]`,
      )
      if (item) {
        this._lastClickedItem = item as HTMLElement
        this._lastClickedItem.focus()
      }
    }
  }

  async updated(changedProperties: Map<string, unknown>) {
    await super.updated(changedProperties)

    if (changedProperties.has('opened') && this.opened) {
      // Initialize keyboard navigation when dropdown opens
      await this.updateComplete
      this._initializeKeyboardNavigation()
    }
  }

  private _initializeKeyboardNavigation() {
    this._keyboardNavigation = createKeyboardNavigationHandler(this, {
      enabledSelector: 'dap-ds-command-item:not([disabled])',
      onItemClick: (item: HTMLElement) => {
        this._lastClickedItem = item
      },
    })

    // Initialize focus after next update
    this.updateComplete.then(() => {
      this._keyboardNavigation?.initializeFocus(
        this._lastClickedItem || undefined,
      )
    })
  }

  private _handleKeyDown(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Home':
      case 'End':
      case 'Enter':
      case ' ':
        if (!this.opened) {
          if (
            event.key === 'ArrowDown' ||
            event.key === 'ArrowUp' ||
            event.key === 'Enter' ||
            event.key === ' '
          ) {
            event.preventDefault()
            this.showDropDown()
          }
          return
        }

        if (this._keyboardNavigation?.handleKeyDown(event)) {
          // Key was handled by navigation manager
          if (event.key === 'Enter' || event.key === ' ') {
            this._handleFocusOut()
          }
        }
        break

      case 'Escape':
        event.preventDefault()
        this._handleFocusOut()
        break
    }
  }

  private _handleMenuItemClick(event: CustomEvent) {
    const { value, closeOnSelect } = event.detail
    this.emit('dds-change', { value })
    if (closeOnSelect === 'true') {
      this._lastClickedItem = event.target as HTMLElement
      this._handleFocusOut()
    }
  }

  private handleClick(event: MouseEvent) {
    // Prevent the default action so that when the command is nested inside an
    // interactive ancestor (e.g. a card rendered as an `<a href>`), toggling
    // the menu does not also activate that ancestor and navigate away.
    // `stopPropagation` alone is not enough: native anchor activation is a
    // default action, not listener propagation.
    event.preventDefault()
    event.stopPropagation()
    this.opened ? this.hideDropDown() : this.showDropDown()
  }

  private handleDocumentMouseDown(event: MouseEvent) {
    const path = event.composedPath()
    if (this && !path.includes(this)) {
      this.hideDropDown()
    }
  }

  async showDropDown() {
    if (this.opened) {
      return
    }

    this.opened = true
    this._trigger[0]?.setAttribute('aria-expanded', 'true')

    document.addEventListener('mousedown', this.handleDocumentMouseDown)

    await this.updateComplete
  }

  async hideDropDown(options?: { skipFocus?: boolean }) {
    if (!this.opened) {
      return
    }

    this.opened = false
    this._trigger[0]?.setAttribute('aria-expanded', 'false')

    document.removeEventListener('mousedown', this.handleDocumentMouseDown)

    // Reset keyboard navigation state
    this._keyboardNavigation?.reset()

    await this.updateComplete

    if (!options?.skipFocus) {
      this.focus()
    }
  }

  private _handleFocusOut() {
    this.hideDropDown()
    this.focus()
  }

  private _handlePopupAutoClose = () => {
    this.hideDropDown({ skipFocus: true })
  }

  public focus() {
    if (this._trigger && this._trigger.length > 0) {
      const triggerElement = this._trigger[0]
      const rect = triggerElement.getBoundingClientRect()
      const isFullyInViewport =
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= window.innerHeight &&
        rect.right <= window.innerWidth

      if (isFullyInViewport) {
        triggerElement.focus({ preventScroll: true })
      }
    }
  }

  private _handleTriggerChange() {
    if (this._trigger && this._trigger.length > 0) {
      this._trigger[0].setAttribute('aria-controls', 'popup')
      this._trigger[0].setAttribute(
        'aria-expanded',
        this.opened ? 'true' : 'false',
      )
    }
  }

  private _handleDefaultSlotChange() {
    this._keyboardNavigation?.invalidateCache()
  }

  render() {
    return html`
      <dap-ds-popup
        part="popup-base"
        id="popup"
        role="menu"
        aria-label=${this.ariaLabel || 'Command menu'}
        aria-labelledby=${this.ariaLabelledBy || nothing}
        aria-orientation="vertical"
        ?opened=${this.opened}
        exportparts="popup"
        class=${clsx('command')}
        placement=${this.placement}
        ?disabled=${this.disabled}
        @click=${this.handleClick}
        maxHeight=${this.maxHeight}
        ?overflow=${this.overflow}
        offset=${this.offset}
        ?sync=${this.sync}
        ?hasArrow=${this.hasArrow}
        ?fullWidth=${this.fullWidth}
        ?disableViewportAutoClose=${this.disableViewportAutoClose}
        floatingStrategy=${this.floatingStrategy}
        @dds-close=${this._handlePopupAutoClose}
        @dds-command-item-click=${this._handleMenuItemClick}>
        <slot
          name="trigger"
          slot="trigger"
          @slotchange=${this._handleTriggerChange}></slot>
        <slot @slotchange=${this._handleDefaultSlotChange}></slot>
      </dap-ds-popup>
    `
  }
}
